import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonService } from '@app/core';
import { NavLinksGroup, SideNavProfileData } from '@app/models';
import { FEATURES, RefUserNavBarLinksData, SIDENAV_DISABLED_ROUTES, SideNavBarLinksData, UI_LINKS, appValues } from '@app/shared/data/values';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    standalone: false
})
export class SidebarComponent implements OnInit, OnChanges {
  @Input('sideNavData') sideNavData!: SideNavProfileData;
  sidebarLinks = UI_LINKS;
  navigationLinks: NavLinksGroup = {...SideNavBarLinksData};
  restrictedRoutes: string[] = [];
  isSidebarLoading$ = new Observable<boolean>();
  constructor(
    private readonly _commonService: CommonService
  ) {
    const envData: any = environment;
    let restrictedRoutes: string[] = envData['restrictedRoutes'] || [];
    if (restrictedRoutes?.length) {
      this.restrictedRoutes = restrictedRoutes.map(routelink => (`/home/${routelink}`));
    }
  }
  
  ngOnInit(): void {
    this.isSidebarLoading$ = this._commonService.isSidebarLoading;
    this.setAccessFlags();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sideNavData'] && !changes['sideNavData'].firstChange) {
      this.setAccessFlags();
    }
  }
  private setAccessFlags() {
    const enterpriseReferralEnabled = !!this._commonService.appProfileDataAsValue?.currentOrganization?.enterprise_referral_enabled;

    // Create a Map of accessible feature names and their access levels for quick lookup
    const userFeatures = new Map(
      this._commonService.appProfileDataAsValue?.user.features.map(feature => [
        feature.featureName,
        feature.access_level,
      ])
    );

    // Helper function to determine if a feature exists in user's features
    const doesFeatureExist = (featureName: string): boolean => userFeatures.has(featureName);

    // Helper function to check if a feature is disabled (exists but has 'no_access')
    const isFeatureDisabled = (featureName: string): boolean =>
      userFeatures.get(featureName) === 'no_access';
    // Map over the navigation links to apply access flags and visibility flags
    this.navigationLinks.navs = SideNavBarLinksData.navs.map(nav => {
      // Check if the main route's feature exists or is disabled
      const isNavHidden = !doesFeatureExist(nav.featureName);
      const isNavDisabled = isNavHidden ? false : isFeatureDisabled(nav.featureName);
  
      // Map over subRoutes to apply access flags individually
      const modifiedSubRoutes = nav.subRoutes?.map(subRoute => {
        const isSubRouteHidden = !doesFeatureExist(subRoute.featureName);
        const isSubRouteDisabled = isSubRouteHidden ? false : isFeatureDisabled(subRoute.featureName);
  
        return {
          ...subRoute,
          isHidden: isSubRouteHidden,
          isDisabled: isSubRouteDisabled,
        };
      });

      // Check if any subroute is enabled (not hidden and not disabled)
      const isAnySubRouteEnabled = modifiedSubRoutes?.some(
        subRoute => !subRoute.isHidden && !subRoute.isDisabled
      );

      // Update parent route properties based on subroutes
      const updatedIsNavDisabled = isNavDisabled && !isAnySubRouteEnabled;

      return {
        ...nav,
        isHidden: isNavHidden,
        isDisabled: updatedIsNavDisabled,
        subRoutes: modifiedSubRoutes || [],
      };
    });
  
    // Repeat the same logic for `admin` and `system` sections
    this.navigationLinks.admin = SideNavBarLinksData.admin.map(adminNav => {
      const isReferralsNav = adminNav.route?.[0] === '/home/referrals';
      // When the org uses the new enterprise referrals module, the nav is gated by
      // the per-user ENTERPRISE_PORTAL feature.
      const isReferralsEnterprise = isReferralsNav && enterpriseReferralEnabled;
      const effectiveFeatureName = isReferralsEnterprise
        ? FEATURES.ENTERPRISE_PORTAL
        : adminNav.featureName;

      // For the enterprise referrals nav, keep the item visible but greyed (disabled)
      // when the user's role has not been granted ENTERPRISE_PORTAL — clicking is
      // blocked, and a direct navigation lands on a friendly access screen instead of
      // the "Access Denied" toast. Every other nav hides when its feature is absent
      // (unchanged behaviour); the legacy referrals "no module" case is still greyed
      // via disabledRoutes/referral_enabled.
      const isAdminHidden = isReferralsEnterprise ? false : !doesFeatureExist(effectiveFeatureName);
      const isAdminDisabled = isReferralsEnterprise
        ? (!doesFeatureExist(effectiveFeatureName) || isFeatureDisabled(effectiveFeatureName))
        : (isAdminHidden ? false : isFeatureDisabled(effectiveFeatureName));

      // Enterprise referrals is a single iframe page — sub-routes don't apply.
      // Return empty array so the template renders a flat link (listItemNavTemp)
      // instead of an expansion panel with all-hidden sub-routes.
      const modifiedAdminSubRoutes = isReferralsEnterprise
        ? []
        : adminNav.subRoutes?.map(subRoute => {
          const isSubRouteHidden = !doesFeatureExist(subRoute.featureName);
          const isSubRouteDisabled = isSubRouteHidden ? false : isFeatureDisabled(subRoute.featureName);

          return {
            ...subRoute,
            isHidden: isSubRouteHidden,
            isDisabled: isSubRouteDisabled,
          };
        });

      // Check if any subroute is enabled (not hidden and not disabled)
      const isAnySubRouteEnabled = modifiedAdminSubRoutes?.some(
        subRoute => !subRoute.isHidden && !subRoute.isDisabled
      );

      // Update parent route properties based on subroutes
      const updatedIsAdminDisabled = isAdminDisabled && !isAnySubRouteEnabled;

      return {
        ...adminNav,
        isHidden: isAdminHidden,
        isDisabled: updatedIsAdminDisabled,
        subRoutes: modifiedAdminSubRoutes,
      };
    });

    this.navigationLinks.system = SideNavBarLinksData.system.map(systemNav => {
      const isSystemHidden = !doesFeatureExist(systemNav.featureName);
      const isSystemDisabled = isSystemHidden ? false : isFeatureDisabled(systemNav.featureName);
  
      const modifiedSystemSubRoutes = systemNav.subRoutes?.map(subRoute => {
        const isSubRouteHidden = !doesFeatureExist(subRoute.featureName);
        const isSubRouteDisabled = isSubRouteHidden ? false : isFeatureDisabled(subRoute.featureName);
  
        return {
          ...subRoute,
          isHidden: isSubRouteHidden,
          isDisabled: isSubRouteDisabled,
        };
      });

      // Check if any subroute is enabled (not hidden and not disabled)
      const isAnySubRouteEnabled = modifiedSystemSubRoutes?.some(
        subRoute => !subRoute.isHidden && !subRoute.isDisabled
      );

      // Update parent route properties based on subroutes
      const updatedIsSystemDisabled = isSystemDisabled && !isAnySubRouteEnabled;

      return {
        ...systemNav,
        isHidden: isSystemHidden,
        isDisabled: updatedIsSystemDisabled,
        subRoutes: modifiedSystemSubRoutes || [],
      };
    });
  }
  
  isAllSubRoutesHidden(subRoutes: any[]): boolean {
    if (!subRoutes || subRoutes.length === 0) {
      return false; // No subRoutes, so not hidden
    }
    return subRoutes.every(subRoute => subRoute.isHidden);
  }
  public isLinkActive(path: string): boolean {
    return ((document.location.href).toString().split('?')[0].endsWith(path));
  }
  isDisabledRoute(path: string[]): boolean {
    return !!this.disabledRoutes.find(dRoute => dRoute == path.join());
  }
  
  isNavVisible(feature : string):boolean{
    // Removing to allow all the organisations to use workflow
    // if(feature===FEATURES.WORKFLOW){
    //   return this.sideNavData.workflow_enabled
    // }
    return true
  }

  get disabledRoutes(): string[] {
    const { restrictedModules, workflow_enabled, referral_enabled } = this.sideNavData;

    return [
      ...(restrictedModules ?? []),
      SIDENAV_DISABLED_ROUTES.NOT_FOUND,
      ...(!workflow_enabled ? [SIDENAV_DISABLED_ROUTES.WORKFLOW] : []),
      ...(!referral_enabled ? [SIDENAV_DISABLED_ROUTES.REFERRALS] : []),
    ];
  }
  get isExternalAdmin() {
      return (this._commonService.appProfileDataAsValue.user.user.role == appValues.externalPortalUser);
    }
}